import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  memo,
} from 'react';
import { aiService, PatientCase } from '../services/aiService';
import { CaseLimitError, apiGet } from '../services/apiService';
import SessionSetup, { SessionConfig } from './SessionSetup';
import './ChatInterface.css';

interface Message {
  id: number;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
}

const MessageBubble = memo(({ message, isSuccess }: { message: Message; isSuccess?: boolean }) => {
  const timeStr = message.timestamp.toLocaleTimeString();

  if (message.sender === 'ai') {
    return (
      <div className={`flex items-start gap-4 max-w-[85%]${isSuccess ? ' border-l-4 border-secondary rounded-l-none' : ''}`}>
        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-600 to-slate-400 flex-shrink-0 flex items-center justify-center overflow-hidden">
          <span className="material-symbols-outlined text-white text-lg">person</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="bg-surface-container-high p-4 rounded-2xl rounded-tl-none text-on-surface text-sm leading-relaxed break-words">
            {message.text}
          </div>
          <div className="text-[0.7rem] text-on-surface-variant/60 mt-1 px-1" aria-label={`Enviado às ${timeStr}`}>{timeStr}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-4 max-w-[85%] ml-auto flex-row-reverse">
      <div className="w-10 h-10 rounded-full bg-primary flex-shrink-0 flex items-center justify-center text-on-primary font-bold font-headline text-xs">Dr</div>
      <div className="flex-1 min-w-0 flex flex-col items-end">
        <div className="bg-primary/10 border border-primary/20 p-4 rounded-2xl rounded-tr-none text-on-surface text-sm leading-relaxed break-words">
          {message.text}
        </div>
        <div className="text-[0.7rem] text-on-surface-variant/60 mt-1 px-1 text-right" aria-label={`Enviado às ${timeStr}`}>{timeStr}</div>
      </div>
    </div>
  );
});

export interface ChatInterfaceRef {
  handleNewSession: () => void;
}

const RARITY_LABELS: Record<string, string> = {
  comum: 'Comum',
  incomum: 'Incomum',
  rara: 'Rara',
  aleatorio: 'Aleatório',
};

const SPECIALTY_LABELS: Record<string, string> = {
  cardiologia: 'Cardiologia',
  pneumologia: 'Pneumologia',
  gastroenterologia: 'Gastroenterologia',
  neurologia: 'Neurologia',
  infectologia: 'Infectologia',
  endocrinologia: 'Endocrinologia',
  nefrologia: 'Nefrologia',
  emergencia: 'Emergência',
  reumatologia: 'Reumatologia',
};

const MAX_DIAGNOSIS_ATTEMPTS = 3;

/** Format seconds into MM:SS */
function formatElapsedTime(totalSeconds: number): string {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/** Extract a short case title from the initial presentation text */
function extractCaseTitle(text: string): string {
  // Take first line or first sentence
  const firstLine = text.split('\n')[0].trim();
  // If it's too long, take up to a period or comma
  if (firstLine.length > 80) {
    const cut = firstLine.substring(0, 80);
    const lastPeriod = cut.lastIndexOf('.');
    const lastComma = cut.lastIndexOf(',');
    const cutPoint = Math.max(lastPeriod, lastComma);
    if (cutPoint > 30) return cut.substring(0, cutPoint) + '...';
    return cut + '...';
  }
  return firstLine;
}

const ChatInterface = forwardRef<ChatInterfaceRef, { onUpgrade: () => void; onCaseLimit: (periodEnd: number) => void; onInvalidSubscription: () => void; planKey?: string }>(({ onUpgrade: _onUpgrade, onCaseLimit, onInvalidSubscription, planKey }, ref) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [currentCase, setCurrentCase] = useState<PatientCase | null>(null);
  const [gameState, setGameState] = useState<'initial' | 'playing' | 'diagnosed' | 'failed'>('initial');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionConfig, setSessionConfig] = useState<SessionConfig | null>(null);
  const [diagnosisAttemptsUsed, setDiagnosisAttemptsUsed] = useState(0);
  const [activeModal, setActiveModal] = useState<'diagnose' | 'exam' | 'feedback' | null>(null);
  const [modalInput, setModalInput] = useState('');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showCaseHint, setShowCaseHint] = useState(false);

  // Sidebar timer state
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Mobile sidebar expand/collapse
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const modalInputRef = useRef<HTMLInputElement>(null);
  // Ref to access latest state inside async callbacks without stale closures
  const currentCaseRef = useRef<PatientCase | null>(null);
  const messagesRef = useRef<Message[]>([]);
  const diagnosisAttemptsRef = useRef(0);
  const msgIdRef = useRef(0);

  useEffect(() => { currentCaseRef.current = currentCase; }, [currentCase]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { diagnosisAttemptsRef.current = diagnosisAttemptsUsed; }, [diagnosisAttemptsUsed]);

  // Timer: count up every second while playing
  useEffect(() => {
    if (sessionStartTime === null) return;
    // Update immediately
    setElapsedSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));

    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [sessionStartTime]);

  // Stop timer when game ends
  useEffect(() => {
    if (gameState === 'diagnosed' || gameState === 'failed') {
      // Timer freezes — we just stop updating by not clearing sessionStartTime
      // The interval cleanup above handles it since we don't reset sessionStartTime
    }
    if (gameState === 'initial') {
      setSessionStartTime(null);
      setElapsedSeconds(0);
    }
  }, [gameState]);

  // Show first-case hint once when the first patient message arrives
  useEffect(() => {
    if (messages.length === 1 && messages[0].sender === 'ai' && !localStorage.getItem('medpuzzle-case-hint')) {
      setShowCaseHint(true);
      const timer = setTimeout(() => {
        setShowCaseHint(false);
        localStorage.setItem('medpuzzle-case-hint', '1');
      }, 12000);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (activeModal && modalInputRef.current) {
      setTimeout(() => modalInputRef.current?.focus(), 50);
    }
  }, [activeModal]);

  const addMessage = useCallback((text: string, sender: 'user' | 'ai') => {
    const id = ++msgIdRef.current;
    setMessages(prev => [...prev, { id, text, sender, timestamp: new Date() }]);
  }, []);

  const startNewCase = useCallback(async (config: SessionConfig) => {
    setIsLoading(true);
    setDiagnosisAttemptsUsed(0);
    try {
      const { status } = await apiGet<{ status: string; planKey: string | null }>('/subscription/status');
      if (status !== 'active' && status !== 'past_due') {
        setIsLoading(false);
        onInvalidSubscription();
        return;
      }
      const patientCase = await aiService.generatePatientCase(config.specialty, config.rarity);
      setCurrentCase(patientCase);
      setGameState('playing');
      setSessionStartTime(Date.now());
      const id = ++msgIdRef.current;
      setMessages([{ id, text: patientCase.initialPresentation, sender: 'ai', timestamp: new Date() }]);
    } catch (err: any) {
      if (err instanceof CaseLimitError) {
        onCaseLimit(err.periodEnd);
        setGameState('initial');
        setSessionConfig(null);
      } else {
        console.error('Case generation error:', err);
        addMessage('Nao foi possivel gerar o caso clinico. Tente novamente.', 'ai');
        setGameState('initial');
        setSessionConfig(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [addMessage, onCaseLimit, onInvalidSubscription]);

  // Auto-start when returning to initial state with an existing config (next case)
  const isStartingRef = useRef(false);
  useEffect(() => {
    if (gameState === 'initial' && sessionConfig !== null && !isLoading && !isStartingRef.current) {
      isStartingRef.current = true;
      startNewCase(sessionConfig).finally(() => { isStartingRef.current = false; });
    }
  }, [gameState, sessionConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetupStart = useCallback((config: SessionConfig) => {
    setSessionConfig(config);
    // The effect above will trigger startNewCase
  }, []);

  const handleNextSameSpecialty = useCallback(() => {
    setMessages([]);
    setCurrentCase(null);
    setDiagnosisAttemptsUsed(0);
    setSidebarExpanded(false);
    setGameState('initial');
  }, []);

  const processUserInput = async (input: string) => {
    const caseSnapshot = currentCaseRef.current;
    if (!caseSnapshot) return;
    addMessage(input, 'user');
    setIsLoading(true);
    try {
      const result = await aiService.generateResponse(
        input,
        JSON.stringify(caseSnapshot),
        JSON.stringify(messagesRef.current.map(m => ({ text: m.text, sender: m.sender }))),
      );
      if (result.responseType === 'error') {
        addMessage(`\u274C ${result.content}`, 'ai');
      } else {
        addMessage(result.content, 'ai');
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      addMessage('Não foi possível enviar a mensagem. Tente novamente.', 'ai');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExamSubmit = useCallback(async () => {
    const caseSnapshot = currentCaseRef.current;
    if (!modalInput.trim() || !caseSnapshot) return;
    const examName = modalInput.trim();
    addMessage(`Solicitando exame: ${examName}`, 'user');
    setIsLoading(true);
    setActiveModal(null);
    setModalInput('');
    try {
      const result = await aiService.generateResponse(
        `[ORDER EXAM] ${examName}`,
        JSON.stringify(caseSnapshot),
        JSON.stringify(messagesRef.current.map(m => ({ text: m.text, sender: m.sender }))),
      );
      addMessage(result.content, 'ai');
    } catch (err: any) {
      console.error('Exam error:', err);
      addMessage('Não foi possível processar o exame. Tente novamente.', 'ai');
    } finally {
      setIsLoading(false);
    }
  }, [modalInput, addMessage]);

  const handleDiagnoseSubmit = useCallback(async () => {
    const caseSnapshot = currentCaseRef.current;
    if (!modalInput.trim() || !caseSnapshot) return;
    if (diagnosisAttemptsRef.current >= MAX_DIAGNOSIS_ATTEMPTS) return;
    const diagnosis = modalInput.trim();
    addMessage(`Diagnóstico: ${diagnosis}`, 'user');
    setIsLoading(true);
    setActiveModal(null);
    setModalInput('');
    try {
      const result = await aiService.checkDiagnosis(diagnosis, JSON.stringify(caseSnapshot));
      const newUsed = diagnosisAttemptsRef.current + 1;
      setDiagnosisAttemptsUsed(newUsed);
      if (result.isCorrect) {
        addMessage(
          `\uD83C\uDF89 Correto! O paciente tem ${caseSnapshot.correctDiagnosis}. ${caseSnapshot.explanation}`,
          'ai',
        );
        setGameState('diagnosed');
        if (sessionConfig) {
          aiService.markDiagnosisComplete(sessionConfig.specialty, caseSnapshot.correctDiagnosis, caseSnapshot.periodStart).catch(() => {});
        }
      } else if (newUsed >= MAX_DIAGNOSIS_ATTEMPTS) {
        addMessage(
          `Tentativas esgotadas. O diagnóstico correto era: **${caseSnapshot.correctDiagnosis}**. ${caseSnapshot.explanation}`,
          'ai',
        );
        setGameState('failed');
        if (sessionConfig) {
          aiService.markDiagnosisComplete(sessionConfig.specialty, caseSnapshot.correctDiagnosis, caseSnapshot.periodStart).catch(() => {});
        }
      } else {
        const remaining = MAX_DIAGNOSIS_ATTEMPTS - newUsed;
        addMessage(
          `Diagnóstico incorreto. Você ainda tem ${remaining} tentativa${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''}. Continue coletando informações.`,
          'ai',
        );
      }
    } catch (err: any) {
      console.error('Diagnosis check error:', err);
      addMessage('Não foi possível verificar o diagnóstico. Tente novamente.', 'ai');
    } finally {
      setIsLoading(false);
    }
  }, [modalInput, sessionConfig, addMessage]);

  const handleNewCase = useCallback(() => {
    setMessages([]);
    setCurrentCase(null);
    setSessionConfig(null);
    setDiagnosisAttemptsUsed(0);
    setSidebarExpanded(false);
    setGameState('initial');
  }, []);

  useImperativeHandle(ref, () => ({ handleNewSession: handleNewCase }));

  const handleFeedbackSubmit = useCallback(async () => {
    const caseSnapshot = currentCaseRef.current;
    try {
      await aiService.submitFeedback(
        feedbackComment,
        JSON.stringify(messagesRef.current.map(m => ({ text: m.text, sender: m.sender }))),
        caseSnapshot ? JSON.stringify(caseSnapshot) : '{}',
      );
    } catch {
      // Feedback failure is non-critical
    }
    setFeedbackSubmitted(true);
  }, [feedbackComment]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && (gameState === 'playing' || gameState === 'diagnosed' || gameState === 'failed')) {
      processUserInput(inputValue.trim());
      setInputValue('');
    }
  };

  const handleModalKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeModal === 'exam') handleExamSubmit();
      else if (activeModal === 'diagnose') handleDiagnoseSubmit();
    }
    if (e.key === 'Escape') { setActiveModal(null); setModalInput(''); }
  };

  const handleModalFocusTrap = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const modal = e.currentTarget;
    const focusable = Array.from(
      modal.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const handleDismissCaseHint = useCallback(() => {
    setShowCaseHint(false);
    localStorage.setItem('medpuzzle-case-hint', '1');
  }, []);

  const handleOpenFeedback = useCallback(() => {
    setFeedbackComment('');
    setFeedbackSubmitted(false);
    setActiveModal('feedback');
  }, []);

  const handleOpenExam = useCallback(() => {
    setActiveModal('exam');
    setModalInput('');
  }, []);

  const handleOpenDiagnose = useCallback(() => {
    setActiveModal('diagnose');
    setModalInput('');
  }, []);

  const handleOverlayClose = useCallback(() => {
    if (activeModal === 'feedback') {
      setActiveModal(null);
      setFeedbackComment('');
      setFeedbackSubmitted(false);
    } else {
      setActiveModal(null);
      setModalInput('');
    }
  }, [activeModal]);

  const handleGiveUp = useCallback(() => {
    const caseSnapshot = currentCaseRef.current;
    if (!caseSnapshot) return;
    addMessage(
      `Você desistiu do caso. O diagnóstico correto era: **${caseSnapshot.correctDiagnosis}**. ${caseSnapshot.explanation}`,
      'ai',
    );
    setGameState('failed');
    if (sessionConfig) {
      aiService.markDiagnosisComplete(sessionConfig.specialty, caseSnapshot.correctDiagnosis, caseSnapshot.periodStart).catch(() => {});
    }
  }, [sessionConfig, addMessage]);

  const diagnosisRemaining = MAX_DIAGNOSIS_ATTEMPTS - diagnosisAttemptsUsed;
  const showSetup = gameState === 'initial' && sessionConfig === null;
  const showLoading = sessionConfig !== null && messages.length === 0;
  const showSidebar = messages.length > 0 && sessionConfig && currentCase;

  // Derive case title
  const caseTitle = currentCase ? extractCaseTitle(currentCase.initialPresentation) : '';

  // Determine attempts color class
  const attemptsColorClass = diagnosisRemaining === 0
    ? 'text-on-surface-variant'
    : diagnosisRemaining === 1
      ? 'text-error'
      : 'text-secondary';

  const renderSidebar = () => {
    if (!sessionConfig || !currentCase) return null;

    return (
      <aside className="lg:col-span-3 flex flex-col gap-6 overflow-y-auto pr-2" aria-label="Informações do caso clínico">
        {/* Desktop layout */}
        <div className="hidden lg:flex lg:flex-col lg:gap-6">
          {/* Header Status Card */}
          <div className="bg-surface-container-low p-6 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="bg-primary-container/20 text-primary px-3 py-1 rounded-full text-xs font-bold font-label uppercase tracking-wider">
                {SPECIALTY_LABELS[sessionConfig.specialty] ?? sessionConfig.specialty}
              </span>
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-secondary text-sm">signal_cellular_alt</span>
                <span className="text-xs font-label text-on-surface-variant">
                  Dificuldade: <span className="text-secondary">{RARITY_LABELS[sessionConfig.rarity] ?? sessionConfig.rarity}</span>
                </span>
              </div>
            </div>
            <h2 className="text-2xl font-headline font-extrabold text-on-surface leading-tight">{caseTitle}</h2>
            <p className="text-sm text-on-surface-variant mt-2">Paciente em consulta</p>
          </div>

          {/* Bento Grid Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-surface-container-high p-4 rounded-xl flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-primary mb-2" aria-hidden="true">timer</span>
              <span className="text-xs font-label text-on-surface-variant uppercase">Tempo Decorrido</span>
              <span className="text-xl font-headline font-bold text-primary tabular-nums" aria-live="off">
                {formatElapsedTime(elapsedSeconds)}
              </span>
            </div>
            <div className="bg-surface-container-high p-4 rounded-xl flex flex-col items-center justify-center text-center">
              <span className="material-symbols-outlined text-secondary mb-2" aria-hidden="true">medical_services</span>
              <span className="text-xs font-label text-on-surface-variant uppercase">Tentativas Restantes</span>
              <span className={`text-xl font-headline font-bold ${attemptsColorClass}`}>
                {diagnosisRemaining}
              </span>
            </div>
          </div>

          {/* Patient History */}
          {currentCase.patientHistory && (
            <div className="bg-surface-container-low p-6 rounded-xl flex-grow overflow-y-auto">
              <h3 className="text-sm font-headline font-bold text-on-surface mb-4 border-l-4 border-primary pl-3">História Clínica</h3>
              <p className="text-sm text-on-surface-variant leading-relaxed whitespace-pre-wrap">{currentCase.patientHistory}</p>
            </div>
          )}
        </div>

        {/* Mobile layout */}
        <div className="flex flex-col lg:hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-surface-container-low border-b border-outline-variant/10">
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <span className="bg-primary-container/20 text-primary px-2 py-0.5 rounded-full text-[0.65rem] font-bold font-label uppercase tracking-wider">
                {SPECIALTY_LABELS[sessionConfig.specialty] ?? sessionConfig.specialty}
              </span>
              <span className="bg-secondary/15 text-secondary px-2 py-0.5 rounded-full text-[0.65rem] font-bold font-label uppercase tracking-wider">
                {RARITY_LABELS[sessionConfig.rarity] ?? sessionConfig.rarity}
              </span>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex items-center gap-1 font-label text-xs text-on-surface-variant">
                <span className="material-symbols-outlined text-sm" aria-hidden="true">timer</span>
                <span className="font-headline font-bold text-sm text-primary tabular-nums">{formatElapsedTime(elapsedSeconds)}</span>
              </div>
              <div className="flex items-center gap-1 font-label text-xs text-on-surface-variant">
                <span className="material-symbols-outlined text-sm" aria-hidden="true">medical_services</span>
                <span className="font-headline font-bold text-sm text-on-surface">{diagnosisRemaining}/{MAX_DIAGNOSIS_ATTEMPTS}</span>
              </div>
            </div>
            <button
              type="button"
              className="flex items-center justify-center w-8 h-8 bg-surface-container-high border-none rounded-lg text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface transition-colors flex-shrink-0"
              onClick={() => setSidebarExpanded(prev => !prev)}
              aria-label={sidebarExpanded ? 'Recolher detalhes do caso' : 'Expandir detalhes do caso'}
              aria-expanded={sidebarExpanded}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" className={`transition-transform duration-300 ${sidebarExpanded ? 'rotate-180' : ''}`}>
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>

          {sidebarExpanded && (
            <div className="flex flex-col gap-3 px-4 py-3 animate-in">
              <h2 className="text-base font-headline font-extrabold text-on-surface leading-tight">{caseTitle}</h2>

              <div className="grid grid-cols-2 gap-2">
                <div className="bg-surface-container-high p-2.5 rounded-xl flex flex-col items-center justify-center text-center">
                  <span className="material-symbols-outlined text-primary mb-1" aria-hidden="true">timer</span>
                  <span className="text-xs font-label text-on-surface-variant uppercase">Tempo Decorrido</span>
                  <span className="text-lg font-headline font-bold text-primary tabular-nums">
                    {formatElapsedTime(elapsedSeconds)}
                  </span>
                </div>
                <div className="bg-surface-container-high p-2.5 rounded-xl flex flex-col items-center justify-center text-center">
                  <span className="material-symbols-outlined text-secondary mb-1" aria-hidden="true">medical_services</span>
                  <span className="text-xs font-label text-on-surface-variant uppercase">Tentativas Restantes</span>
                  <span className={`text-lg font-headline font-bold ${attemptsColorClass}`}>
                    {diagnosisRemaining}
                  </span>
                </div>
              </div>

              {currentCase.patientHistory && (
                <div className="bg-surface-container-low p-6 rounded-xl overflow-y-auto">
                  <h3 className="text-sm font-headline font-bold text-on-surface mb-4 border-l-4 border-primary pl-3">História Clínica</h3>
                  <p className="text-sm text-on-surface-variant leading-relaxed whitespace-pre-wrap">{currentCase.patientHistory}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </aside>
    );
  };

  const renderChatContent = () => (
    <section className="lg:col-span-9 flex flex-col bg-surface-container-lowest rounded-2xl overflow-hidden shadow-2xl shadow-black/40 border border-outline-variant/10">
      {/* Toolbar */}
      {gameState === 'playing' && (
        <div className="bg-surface-container p-4 flex items-center justify-between border-b border-outline-variant/5 flex-shrink-0 gap-2">
          <div className="flex gap-2">
<button type="button" className="bg-surface-container-highest text-on-surface px-4 py-2 rounded-lg text-xs font-bold font-label flex items-center gap-2 hover:bg-surface-bright transition-colors border-none cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleOpenExam} disabled={isLoading}>
              <span className="material-symbols-outlined text-sm">biotech</span>
              <span className="hidden sm:inline">Solicite exames</span>
            </button>
          </div>
          <button
            type="button"
            className="bg-primary-container text-on-primary-container px-4 py-2 rounded-lg text-xs font-extrabold font-label flex items-center gap-2 hover:opacity-90 active:scale-95 transition-all border-none cursor-pointer whitespace-nowrap flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleOpenDiagnose}
            disabled={isLoading || diagnosisRemaining <= 0}
            aria-label={`Diagnosticar \u2014 ${diagnosisRemaining} tentativa${diagnosisRemaining !== 1 ? 's' : ''} restante${diagnosisRemaining !== 1 ? 's' : ''}`}
          >
            <span className="material-symbols-outlined text-sm">check_circle</span>
            <span className="hidden sm:inline">Diagnosticar ({diagnosisRemaining} tentativa{diagnosisRemaining !== 1 ? 's' : ''})</span>
          </button>
        </div>
      )}

      {/* Chat Messages */}
      <div
        className="flex-grow p-6 overflow-y-auto space-y-6"
        role="log"
        aria-live="polite"
        aria-atomic="false"
        aria-label="Conversa com paciente"
      >
        {/* System message at chat start */}
        {messages.length > 0 && (
          <div className="flex justify-center">
            <span className="bg-surface-variant/30 text-[10px] text-on-surface-variant px-3 py-1 rounded-full uppercase tracking-widest font-bold">Início da Simulação Clínica</span>
          </div>
        )}

        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            message={message}
            isSuccess={
              gameState === 'diagnosed' &&
              message.sender === 'ai' &&
              index === messages.length - 1
            }
          />
        ))}
        {isLoading && messages.length > 0 && (
          <div className="flex items-start gap-4 max-w-[85%]">
            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-600 to-slate-400 flex-shrink-0 flex items-center justify-center overflow-hidden">
              <span className="material-symbols-outlined text-white text-lg">person</span>
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="bg-surface-container-high p-4 rounded-2xl rounded-tl-none flex items-center gap-1"
                role="status"
                aria-label="Paciente está respondendo..."
              >
                <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant animate-bounce" style={{ animationDelay: '200ms' }}></span>
                <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant animate-bounce" style={{ animationDelay: '400ms' }}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat Footer / Input Area */}
      <div className="p-6 bg-surface-container-low flex-shrink-0">
        {(gameState === 'diagnosed' || gameState === 'failed') && sessionConfig && (
          <div className="flex flex-col items-stretch gap-2 mb-4">
            <button type="button" className="flex items-center justify-center gap-2 py-3 px-5 bg-primary text-on-primary border-none rounded-lg text-sm font-semibold font-body cursor-pointer hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleNextSameSpecialty} disabled={isLoading}>
              <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
              Próximo caso — {SPECIALTY_LABELS[sessionConfig.specialty] ?? sessionConfig.specialty}
            </button>
            <div className="flex items-center justify-center gap-2 p-2 text-on-surface-variant text-xs">
              <span className="material-symbols-outlined text-base" aria-hidden="true">info</span>
              Apenas simulação educacional — não é aconselhamento médico real
            </div>
          </div>
        )}

        {gameState === 'playing' && showCaseHint && (
          <div className="flex items-center justify-between gap-2 px-3.5 py-2 bg-surface-container border border-outline-variant/20 rounded-lg mb-2" role="note">
            <div className="text-xs text-on-surface-variant leading-snug flex-1">
              <span>Pergunte ao paciente, depois solicite exames e diagnostique (3 tentativas)</span>
            </div>
            <button
              type="button"
              className="bg-transparent border-none cursor-pointer text-lg text-on-surface-variant px-1 py-0.5 rounded hover:text-on-surface hover:bg-surface-container-high transition-colors flex-shrink-0"
              onClick={handleDismissCaseHint}
              aria-label="Fechar dica"
            >
              ×
            </button>
          </div>
        )}

        {(gameState === 'playing' || gameState === 'diagnosed' || gameState === 'failed') && (
          <form onSubmit={handleSubmit} className="chat-input-form w-full">
            <div className="input-container relative w-full">
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder={gameState === 'playing' ? 'Faça perguntas ao paciente...' : 'Faça perguntas sobre o caso...'}
                className="w-full bg-surface-container-highest border-none rounded-2xl py-4 pl-6 pr-16 text-on-surface placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/50 transition-all font-body text-sm outline-none"
                disabled={isLoading}
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 bg-primary text-on-primary p-2 rounded-xl hover:scale-105 active:scale-95 transition-all border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading || !inputValue.trim()} aria-label="Enviar mensagem">
                <span className="material-symbols-outlined">send</span>
              </button>
            </div>
          </form>
        )}

        {/* Below-input action links */}
        {gameState === 'playing' && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex gap-4">
              <button type="button" className="text-xs font-label text-on-surface-variant flex items-center gap-1 hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0" onClick={handleOpenFeedback}>
                <span className="material-symbols-outlined text-base">analytics</span>
                Feedback
              </button>
              <button type="button" className="text-xs font-label text-on-surface-variant flex items-center gap-1 hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0 disabled:opacity-50" onClick={handleOpenExam} disabled={isLoading}>
                <span className="material-symbols-outlined text-base">emergency_share</span>
                Solicitar Exame
              </button>
            </div>
            <button type="button" className="text-xs font-label text-on-surface-variant flex items-center gap-1 hover:text-error transition-colors bg-transparent border-none cursor-pointer p-0" onClick={handleGiveUp}>
              <span className="material-symbols-outlined text-base">flag</span>
              Desistir do Caso
            </button>
          </div>
        )}
      </div>
    </section>
  );

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {showSetup && <SessionSetup onStart={handleSetupStart} planKey={planKey} />}

      {showLoading && (
        <div className="flex flex-col items-center justify-center flex-1 gap-4">
          <div className="flex items-center gap-2 text-on-surface-variant text-sm font-label">
            <span>{SPECIALTY_LABELS[sessionConfig!.specialty] ?? sessionConfig!.specialty}</span>
            <span>·</span>
            <span>{RARITY_LABELS[sessionConfig!.rarity] ?? sessionConfig!.rarity}</span>
          </div>
          <p className="text-on-surface-variant text-sm">{isLoading ? 'Gerando caso clínico...' : 'Conectando ao servidor...'}</p>
        </div>
      )}

      {showSidebar ? (
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0 overflow-hidden">
          {renderSidebar()}
          {renderChatContent()}
        </div>
      ) : !showSetup && (
        <>
          {/* No sidebar — original layout for non-chat states */}
          {messages.length > 0 && sessionConfig && (
            <div className="flex items-center justify-center gap-2 py-2 text-on-surface-variant text-sm font-label">
              <span className="text-primary font-bold">
                {SPECIALTY_LABELS[sessionConfig.specialty] ?? sessionConfig.specialty}
              </span>
              <span>·</span>
              <span className="bg-secondary/15 text-secondary px-2 py-0.5 rounded-full text-xs font-bold uppercase">
                {RARITY_LABELS[sessionConfig.rarity] ?? sessionConfig.rarity}
              </span>
            </div>
          )}

          <div
            className="flex-1 p-6 overflow-y-auto flex flex-col gap-6"
            role="log"
            aria-live="polite"
            aria-atomic="false"
            aria-label="Conversa com paciente"
          >
            {messages.map((message, index) => (
              <MessageBubble
                key={message.id}
                message={message}
                isSuccess={
                  gameState === 'diagnosed' &&
                  message.sender === 'ai' &&
                  index === messages.length - 1
                }
              />
            ))}
            {isLoading && messages.length > 0 && (
              <div className="flex items-start gap-4 max-w-[85%]">
                <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-600 to-slate-400 flex-shrink-0 flex items-center justify-center overflow-hidden">
                  <span className="material-symbols-outlined text-white text-lg">person</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className="bg-surface-container-high p-4 rounded-2xl rounded-tl-none flex items-center gap-1"
                    role="status"
                    aria-label="Paciente está respondendo..."
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant animate-bounce" style={{ animationDelay: '200ms' }}></span>
                    <span className="w-1.5 h-1.5 rounded-full bg-on-surface-variant animate-bounce" style={{ animationDelay: '400ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-6 bg-surface-container-low flex-shrink-0">
            {(gameState === 'diagnosed' || gameState === 'failed') && sessionConfig && (
              <div className="flex flex-col items-stretch gap-2 mb-4">
                <button type="button" className="flex items-center justify-center gap-2 py-3 px-5 bg-primary text-on-primary border-none rounded-lg text-sm font-semibold font-body cursor-pointer hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleNextSameSpecialty} disabled={isLoading}>
                  <span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>
                  Próximo caso — {SPECIALTY_LABELS[sessionConfig.specialty] ?? sessionConfig.specialty}
                </button>
                <div className="flex items-center justify-center gap-2 p-2 text-on-surface-variant text-xs">
                  <span className="material-symbols-outlined text-base" aria-hidden="true">info</span>
                  Apenas simulação educacional — não é aconselhamento médico real
                </div>
              </div>
            )}

            {gameState === 'playing' && showCaseHint && (
              <div className="flex items-center justify-between gap-2 px-3.5 py-2 bg-surface-container border border-outline-variant/20 rounded-lg mb-2" role="note">
                <div className="text-xs text-on-surface-variant leading-snug flex-1">
                  <span>Pergunte ao paciente, depois solicite exames e diagnostique (3 tentativas)</span>
                </div>
                <button
                  type="button"
                  className="bg-transparent border-none cursor-pointer text-lg text-on-surface-variant px-1 py-0.5 rounded hover:text-on-surface hover:bg-surface-container-high transition-colors flex-shrink-0"
                  onClick={handleDismissCaseHint}
                  aria-label="Fechar dica"
                >
                  ×
                </button>
              </div>
            )}

            {gameState === 'playing' && (
              <div className="flex items-center justify-between mt-4">
                <div className="flex gap-4">
                  <button type="button" className="text-xs font-label text-on-surface-variant flex items-center gap-1 hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0" onClick={handleOpenFeedback}>
                    <span className="material-symbols-outlined text-base">analytics</span>
                    Feedback
                  </button>
                  <button type="button" className="text-xs font-label text-on-surface-variant flex items-center gap-1 hover:text-primary transition-colors bg-transparent border-none cursor-pointer p-0 disabled:opacity-50" onClick={handleOpenExam} disabled={isLoading}>
                    <span className="material-symbols-outlined text-base">emergency_share</span>
                    Solicitar Exame
                  </button>
                </div>
                <button type="button" className="text-xs font-label text-on-surface-variant flex items-center gap-1 hover:text-error transition-colors bg-transparent border-none cursor-pointer p-0" onClick={handleGiveUp}>
                  <span className="material-symbols-outlined text-base">flag</span>
                  Desistir do Caso
                </button>
              </div>
            )}

            {(gameState === 'playing' || gameState === 'diagnosed' || gameState === 'failed') && (
              <form onSubmit={handleSubmit} className="chat-input-form w-full">
                <div className="input-container relative w-full">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    placeholder={gameState === 'playing' ? 'Faça perguntas ao paciente...' : 'Faça perguntas sobre o caso...'}
                    className="w-full bg-surface-container-highest border-none rounded-2xl py-4 pl-6 pr-16 text-on-surface placeholder:text-on-surface-variant focus:ring-2 focus:ring-primary/50 transition-all font-body text-sm outline-none"
                    disabled={isLoading}
                  />
                  <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 bg-primary text-on-primary p-2 rounded-xl hover:scale-105 active:scale-95 transition-all border-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading || !inputValue.trim()} aria-label="Enviar mensagem">
                    <span className="material-symbols-outlined">send</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}

      {activeModal && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
          onClick={handleOverlayClose}
        >
          <div
            className="bg-surface-container-high p-8 rounded-2xl max-w-md w-full mx-4 flex flex-col gap-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="action-modal-title"
            onClick={e => e.stopPropagation()}
            onKeyDown={handleModalFocusTrap}
          >
            {activeModal === 'feedback' ? (
              <>
                {feedbackSubmitted ? (
                  <div className="flex flex-col items-center gap-4 text-center">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                      <circle cx="20" cy="20" r="19" fill="#ECFDF5" stroke="#10B981" strokeWidth="1.5" />
                      <path d="M12 20l6 6 10-12" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <h3 id="action-modal-title" className="text-lg font-headline font-bold text-on-surface">Obrigado pelo feedback!</h3>
                    <p className="text-sm text-on-surface-variant">Seu comentário e o histórico da conversa foram enviados para análise.</p>
                    <button className="w-full py-3 bg-primary text-on-primary rounded-xl font-semibold font-body text-sm border-none cursor-pointer hover:opacity-90 transition-opacity" onClick={handleOverlayClose}>Fechar</button>
                  </div>
                ) : (
                  <>
                    <h3 id="action-modal-title" className="text-lg font-headline font-bold text-on-surface">Enviar Feedback</h3>
                    <p className="text-sm text-on-surface-variant">Adicione um comentário sobre esta sessão.</p>
                    <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg text-xs text-on-surface-variant">
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true" className="flex-shrink-0 mt-0.5">
                        <circle cx="8" cy="8" r="7" stroke="#3B82F6" strokeWidth="1.2" />
                        <path d="M8 5v3M8 10h0" stroke="#3B82F6" strokeWidth="1.3" strokeLinecap="round" />
                      </svg>
                      O chat completo desta sessão será incluído no envio para análise.
                    </div>
                    <textarea
                      className="w-full bg-surface-container-highest border border-outline-variant/20 rounded-xl p-4 text-on-surface placeholder:text-on-surface-variant text-sm font-body resize-none focus:ring-2 focus:ring-primary/50 outline-none"
                      value={feedbackComment}
                      onChange={e => setFeedbackComment(e.target.value)}
                      placeholder="Ex.: O paciente não respondeu coerentemente..."
                      rows={4}
                    />
                    <div className="flex gap-3 justify-end">
                      <button className="px-5 py-2.5 bg-surface-container-highest text-on-surface rounded-xl font-semibold font-body text-sm border-none cursor-pointer hover:bg-surface-bright transition-colors" onClick={handleOverlayClose}>Cancelar</button>
                      <button className="px-5 py-2.5 bg-primary text-on-primary rounded-xl font-semibold font-body text-sm border-none cursor-pointer hover:opacity-90 transition-opacity" onClick={handleFeedbackSubmit}>Enviar</button>
                    </div>
                  </>
                )}
              </>
            ) : activeModal === 'exam' ? (
              <>
                <h3 id="action-modal-title" className="text-lg font-headline font-bold text-on-surface">Solicitar Exame</h3>
                <p className="text-sm text-on-surface-variant">Informe o exame que deseja solicitar para o paciente.</p>
                <input
                  ref={modalInputRef}
                  type="text"
                  value={modalInput}
                  onChange={e => setModalInput(e.target.value)}
                  onKeyDown={handleModalKeyDown}
                  placeholder="Ex.: Hemograma completo, Raio-X de tórax, ECG..."
                  className="w-full bg-surface-container-highest border border-outline-variant/20 rounded-xl py-3 px-4 text-on-surface placeholder:text-on-surface-variant text-sm font-body focus:ring-2 focus:ring-primary/50 outline-none"
                />
                <div className="flex gap-3 justify-end">
                  <button className="px-5 py-2.5 bg-surface-container-highest text-on-surface rounded-xl font-semibold font-body text-sm border-none cursor-pointer hover:bg-surface-bright transition-colors" onClick={handleOverlayClose}>Cancelar</button>
                  <button className="px-5 py-2.5 bg-primary text-on-primary rounded-xl font-semibold font-body text-sm border-none cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleExamSubmit} disabled={!modalInput.trim()}>Solicitar</button>
                </div>
              </>
            ) : (
              <>
                <h3 id="action-modal-title" className="text-lg font-headline font-bold text-on-surface">Submeter Diagnóstico</h3>
                <p className={`text-sm font-label ${diagnosisRemaining === 1 ? 'text-error' : 'text-on-surface-variant'}`}>
                  {diagnosisRemaining} tentativa{diagnosisRemaining !== 1 ? 's' : ''} restante{diagnosisRemaining !== 1 ? 's' : ''}
                </p>
                <input
                  ref={modalInputRef}
                  type="text"
                  value={modalInput}
                  onChange={e => setModalInput(e.target.value)}
                  onKeyDown={handleModalKeyDown}
                  placeholder="Ex.: Infarto agudo do miocárdio, Pneumonia..."
                  className="w-full bg-surface-container-highest border border-outline-variant/20 rounded-xl py-3 px-4 text-on-surface placeholder:text-on-surface-variant text-sm font-body focus:ring-2 focus:ring-primary/50 outline-none"
                />
                <div className="flex gap-3 justify-end">
                  <button className="px-5 py-2.5 bg-surface-container-highest text-on-surface rounded-xl font-semibold font-body text-sm border-none cursor-pointer hover:bg-surface-bright transition-colors" onClick={handleOverlayClose}>Cancelar</button>
                  <button className="px-5 py-2.5 bg-primary-container text-on-primary-container rounded-xl font-extrabold font-body text-sm border-none cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" onClick={handleDiagnoseSubmit} disabled={!modalInput.trim()}>Confirmar</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

ChatInterface.displayName = 'ChatInterface';
export default ChatInterface;
